import { onUnmounted, ref } from 'vue'

// Types
type ObserverCallback = (entry: IntersectionObserverEntry) => void

interface ObserverOptions {
  root?: Element | null
  rootMargin?: string
  threshold?: number | number[]
}

// Store for callbacks mapped by element
const elementCallbacks = new Map<Element, ObserverCallback>()

// Single shared IntersectionObserver instance
let sharedObserver: IntersectionObserver | null = null
let observerRefCount = 0

// Default options
const defaultOptions: ObserverOptions = {
  root: null,
  rootMargin: '0px',
  threshold: 0.5,
}

// Initialize the shared observer
const initializeObserver = (options: ObserverOptions = {}) => {
  if (sharedObserver) return sharedObserver

  const finalOptions = { ...defaultOptions, ...options }

  sharedObserver = new IntersectionObserver((entries) => {
    // Dispatch to appropriate callbacks
    entries.forEach((entry) => {
      const callback = elementCallbacks.get(entry.target)
      if (callback) {
        callback(entry)
      }
    })
  }, finalOptions)

  return sharedObserver
}

// Cleanup observer when no longer needed
const cleanupObserver = () => {
  if (observerRefCount === 0 && sharedObserver) {
    sharedObserver.disconnect()
    sharedObserver = null
    elementCallbacks.clear()
  }
}

/**
 * Shared IntersectionObserver composable
 * Manages a single observer instance shared across multiple components
 * This reduces the number of observers from N (one per component) to 1
 */
export const useSharedIntersectionObserver = (options: ObserverOptions = {}) => {
  // Track if this instance is currently observing
  const isObserving = ref(false)
  const observedElements = new Set<Element>()

  // Observe an element
  const observe = (element: Element, callback: ObserverCallback) => {
    if (!process.client) return

    // Initialize observer if needed
    if (!sharedObserver) {
      initializeObserver(options)
    }

    // Store callback
    elementCallbacks.set(element, callback)

    // Start observing
    sharedObserver?.observe(element)
    observedElements.add(element)

    // Increment ref count
    observerRefCount++
    isObserving.value = true
  }

  // Unobserve an element
  const unobserve = (element: Element) => {
    if (!sharedObserver || !observedElements.has(element)) return

    // Stop observing
    sharedObserver.unobserve(element)

    // Clean up
    elementCallbacks.delete(element)
    observedElements.delete(element)

    // Decrement ref count
    observerRefCount--

    // Cleanup if no more observers
    if (observerRefCount === 0) {
      cleanupObserver()
      isObserving.value = false
    }
  }

  // Unobserve all elements for this instance
  const disconnect = () => {
    observedElements.forEach((element) => {
      unobserve(element)
    })
    observedElements.clear()
    isObserving.value = false
  }

  // Cleanup on unmount
  onUnmounted(() => {
    disconnect()
  })

  return {
    observe,
    unobserve,
    disconnect,
    isObserving,
  }
}

/**
 * Performance metrics for monitoring
 */
export const getObserverMetrics = () => {
  return {
    activeObservers: sharedObserver ? 1 : 0,
    trackedElements: elementCallbacks.size,
    refCount: observerRefCount,
  }
}
